import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useState } from "react";
import { DownloadLinkEnh } from "../../../../components/common/DownloadLink/DownloadLinkEnh";
import FileUploadField from "../../../../components/forms/FileUploadField";
import Auth from "../../../../services/Auth/Auth";
import FilesService from "../../../../services/Integration/files/FilesService";
import RevisiMahasiswaService from "../../../../services/Integration/mahasiswa-sidang/RevisiMahasiswaService";
import { toTitleCase } from "../../../../utils/helpers/common.helpers";
import { FileHelpers } from "../../../../utils/helpers/File.helpers";

const BERKAS_REVISI_KEY = "berkas_revisi";
const BERKAS_CATATAN_KEY = "berkas_catatan";
const UPLOADED_FILE_INITIAL = {
  [BERKAS_REVISI_KEY]: null,
  [BERKAS_CATATAN_KEY]: null,
};

export default function FormRevisiMahasiswa({
  open,
  setOpen,
  refreshRevisiDosen,
  mKSpesialId,
  dosenId,
  revisiDosen,
  nomorRevisi,
}) {
  const user = Auth.getCurrentUser();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [showAlert, setShowAlert] = useState({ success: false, fail: false });
  const [uploadedFiles, setUploadedFiles] = useState(UPLOADED_FILE_INITIAL);

  const handleFileUpload = (e, maxFileSize) => {
    if (!e.target.files) {
      return;
    }
    const uploadedFile = e.target.files[0];

    //dibagi 1048576 alias 1024**2 agar menjadi MegaByte
    if (uploadedFile.size / 1048576 > maxFileSize) {
      setShowAlert({ ...showAlert, fail: true });
    }
    setUploadedFiles({ ...uploadedFiles, [e.target.name]: uploadedFile });
  };

  const resetUploadedFile = (uploadedFileKey) => {
    setUploadedFiles({ ...uploadedFiles, [uploadedFileKey]: null });
  };

  const getFilePrefix = (jenisBerkas) => {
    return `${toTitleCase(jenisBerkas.replace("_", " ")).replace(
      " ",
      ""
    )}_${mKSpesialId}_${nomorRevisi}_${dosenId}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoadingSubmit(true);
    let fileBase64 = {};
    let fileUploadResponseData = {};

    // Upload File to Server
    await Promise.all(
      Object.entries(uploadedFiles).map(async (file) => {
        const jenisBerkas = file[0];
        const fileObj = file[1];
        if (!fileObj) return;

        await FileHelpers.fileToBase64(fileObj)
          .then(
            (result) => (fileBase64 = { ...fileBase64, [jenisBerkas]: result })
          )
          .catch((error) => console.log(error));

        fileUploadResponseData[jenisBerkas] = await FilesService.uploadFile({
          userId: user.id,
          file: fileBase64[jenisBerkas],
          prefix: getFilePrefix(jenisBerkas),
          mKSpesialId: mKSpesialId,
        })
          .then((response) => {
            if (response.status === 201) {
              return response.data;
            } else {
              setShowAlert({ ...showAlert, fail: true });
              return null;
            }
          })
          .catch((error) => {
            console.log(error);
            setShowAlert({ ...showAlert, fail: true });
            return null;
          });

        if (!fileUploadResponseData) {
          setIsLoadingSubmit(false);
          setShowAlert({ ...showAlert, fail: true });
          return;
        }
      })
    );

    // Save revisi as Object
    const payload = {
      [BERKAS_REVISI_KEY]: fileUploadResponseData[BERKAS_REVISI_KEY].id,
      nama_berkas_revisi: fileUploadResponseData[BERKAS_REVISI_KEY].nama_file,
      [BERKAS_CATATAN_KEY]: fileUploadResponseData[BERKAS_CATATAN_KEY].id,
      nama_berkas_catatan: fileUploadResponseData[BERKAS_CATATAN_KEY].nama_file,
      mk_spesial: parseInt(mKSpesialId),
      revisi_dosen: revisiDosen.id,
    };

    await RevisiMahasiswaService.createRevisiMahasiswa(payload)
      .then(async (response) => {
        if (response.status === 200) {
          setShowAlert({ ...showAlert, success: true });
          setOpen(false);
          await refreshRevisiDosen();
          setShowAlert({ ...showAlert, success: false });
        } else {
          setShowAlert({ ...showAlert, fail: true });
        }
      })
      .catch((error) => {
        console.log(error);
        setShowAlert({ ...showAlert, fail: true });
      });

    setUploadedFiles(UPLOADED_FILE_INITIAL);
    setIsLoadingSubmit(false);
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Formulir Revisi ke-{nomorRevisi}</DialogTitle>
      <DialogContent>
        <div className="my-2 flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2">
              <div>Catatan Revisi Dosen:</div>
              <div>
                {revisiDosen &&
                  (revisiDosen.catatanRevisi ?? "Tidak ada catatan revisi.")}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <div>Berkas Catatan Revisi Dosen:</div>
              <div>
                {revisiDosen && revisiDosen.namaBerkasRevisi ? (
                  <DownloadLinkEnh
                    endpoint={`/file/berkas/get/${revisiDosen.namaBerkasRevisi}/`}
                    label={"Unduh"}
                  />
                ) : (
                  "Tidak ada berkas catatan revisi"
                )}
              </div>
            </div>
          </div>
          <hr />
          <small className="text-red-500">* Wajib</small>
          <form className="flex flex-col" onSubmit={handleSubmit}>
            <FileUploadField
              title={"Berkas Revisi"}
              inputName={BERKAS_REVISI_KEY}
              acceptedFileTypes={".pdf"}
              onFileUpload={(e) => handleFileUpload(e, 100)}
              onResetUploadedFile={() => resetUploadedFile(BERKAS_REVISI_KEY)}
              uploadedFile={uploadedFiles[BERKAS_REVISI_KEY]}
              required={true}
              maxFileSize={"100MB"}
            />
            <div className="my-2" />
            <FileUploadField
              title={"Berkas Catatan"}
              deskripsi={
                "Setidaknya berisikan penjelasan revisi apa saja yang dilakukan"
              }
              inputName={BERKAS_CATATAN_KEY}
              acceptedFileTypes={".pdf"}
              onFileUpload={(e) => handleFileUpload(e, 100)}
              onResetUploadedFile={() => resetUploadedFile(BERKAS_CATATAN_KEY)}
              uploadedFile={uploadedFiles[BERKAS_CATATAN_KEY]}
              required={true}
              maxFileSize={"100MB"}
            />
            <Collapse in={showAlert.success || showAlert.fail}>
              <div className="my-2" />
            </Collapse>
            <Collapse in={showAlert.success}>
              <Alert
                onClose={() => setShowAlert({ ...showAlert, success: false })}
              >
                Berkas revisi berhasil diunggah!
              </Alert>
            </Collapse>
            <Collapse in={showAlert.fail}>
              <Alert
                severity="error"
                onClose={() => setShowAlert({ ...showAlert, fail: false })}
              >
                Terjadi permasalahan saat mengunggah. Silakan coba beberapaa
                saat lagi.
              </Alert>
            </Collapse>
            <p className="text-orange-500 py-2">
              Perhatian! Hasil pengumpulan tidak dapat diubah sehingga pastikan
              mengunggah berkas yang seharusnya
            </p>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isLoadingSubmit}
              disabled={isLoadingSubmit || showAlert.success}
            >
              Simpan
            </LoadingButton>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
